import React from 'react';
import { isIE } from 'react-device-detect';

import useElementHeight from './useElementHeight';

/**
 * Make an element stick on top of its parent. Only `sticky` is mandatory.
 * @param {{
 *   sticky: React.RefObject<HTMLElement|null>,
 *   reference?: React.RefObject<HTMLElement|null>,
 *   container?: React.RefObject<HTMLElement|null>,
 *   offsetTop?: number,
 *   pageHeader?: boolean,
 * }} params
 * - `sticky`: the element that will stick on top of its container.
 * - `reference`: the element used to position sticky.
 * - `container`: the container of sticky.
 * - `offsetTop`: fixed offset overriding the reference position.
 * - `pageHeader`: apply the height based on the header height when the sticky is a page header.
 */
const useStickyElement = ({ sticky, reference, container, offsetTop, pageHeader }) => {
  const headerHeight = useElementHeight('#header');
  const bannerHeight = useElementHeight('#impersonate-banner');

  /* eslint-disable-next-line consistent-return */
  React.useLayoutEffect(() => {
    const stickyElement = sticky?.current;

    if (stickyElement) {
      let containerElement = container?.current;
      if (!containerElement) {
        containerElement = stickyElement.parentNode;
      }

      if (!isIE) {
        stickyElement.classList.add('sticky');
      }
      containerElement.classList.add('sticky-container');
      if (pageHeader) {
        containerElement.style.overflow = 'auto';
        containerElement.style.height = `calc(100vh - ${headerHeight}px - ${bannerHeight}px)`;
        containerElement.style.maxHeight = `calc(100vh - ${headerHeight}px - ${bannerHeight}px)`;
      }

      let referenceElement = reference?.current;
      if (!referenceElement) {
        referenceElement = stickyElement;
      }

      const setHeaderStickyPosition = () => {
        if (referenceElement || offsetTop) {
          const top = offsetTop ?? referenceElement?.offsetTop;
          stickyElement.style.top = `-${top}px`;
        }
      };

      containerElement.addEventListener('scroll', setHeaderStickyPosition, { passive: true });
      return () => containerElement.removeEventListener('scroll', setHeaderStickyPosition);
    }
  }, [sticky, reference, container, headerHeight, pageHeader, offsetTop]);
};

export default useStickyElement;
